/**
 * IVF Success Rate Prediction Engine
 *
 * Estimates personalized live birth probability by adjusting a clinic's
 * reported base rate with published odds ratios for patient-specific factors.
 *
 * See docs/features/IVF_PREDICTION_MODEL.md for full methodology and sources.
 */

import type { Clinic, ClinicSuccessRates } from "../clinics.types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DiagnosisCategory =
  | "diminished_ovarian_reserve"
  | "male_factor"
  | "endometriosis"
  | "pcos"
  | "tubal_factor"
  | "uterine_factor"
  | "unexplained"
  | "multiple_factors";

export type PredictionInput = {
  age: number;
  diagnoses: DiagnosisCategory[];
  priorFailedIvfCycles: number;
  isUsingDonorEggs: boolean;
  bmi: "underweight" | "normal" | "overweight" | null;
  isSmoking: boolean;
  isFormerSmoker: boolean;
  hasPriorLiveBirth: boolean;
  hasPriorMiscarriage: boolean;
};

export type Adjustment = {
  factor: string;
  direction: "increase" | "decrease" | "neutral";
  oddsRatio: number;
};

export type DataQuality = "high" | "moderate" | "low";

export type PredictionResult = {
  estimatedRate: number;
  confidenceLow: number;
  confidenceHigh: number;
  baseRate: number;
  baseRateLabel: string;
  adjustments: Adjustment[];
  dataQuality: DataQuality;
};

// ---------------------------------------------------------------------------
// Published Odds Ratios
// ---------------------------------------------------------------------------

/**
 * Diagnosis adjustment factors.
 *
 * Sources:
 * - McLernon DJ et al. BMJ 2016;355:i5735
 * - Luke B et al. NEJM 2012;366(26):2483-2491
 */
const DIAGNOSIS_OR: Record<DiagnosisCategory, number> = {
  unexplained: 1.0,
  pcos: 1.15,
  male_factor: 1.08,
  endometriosis: 0.85,
  diminished_ovarian_reserve: 0.7,
  tubal_factor: 0.92,
  uterine_factor: 0.8,
  multiple_factors: 0.7, // conservative: use lowest single-factor OR
};

const DIAGNOSIS_LABELS: Record<DiagnosisCategory, string> = {
  unexplained: "Unexplained infertility",
  pcos: "PMOS (ex PCOS)",
  male_factor: "Male factor",
  endometriosis: "Endometriosis",
  diminished_ovarian_reserve: "Low ovarian reserve",
  tubal_factor: "Tubal factor",
  uterine_factor: "Uterine factor",
  multiple_factors: "Multiple factors",
};

/**
 * BMI adjustment factors.
 * Source: Rittenberg V et al. Reprod Biomed Online 2011;23(4):421-439
 */
const BMI_OR: Record<string, number> = {
  normal: 1.0,
  overweight: 0.9,
  underweight: 0.85,
};

/**
 * Smoking adjustment factors.
 * Source: Waylen AL et al. Hum Reprod Update 2009;15(1):31-44
 */
const SMOKING_ACTIVE_OR = 0.72;
const SMOKING_FORMER_OR = 0.9;

/**
 * Prior pregnancy adjustment factors.
 * Source: McLernon 2016
 */
const PRIOR_LIVE_BIRTH_OR = 1.2;
const PRIOR_MISCARRIAGE_ONLY_OR = 1.05;

/**
 * Per-failed-cycle adjustment.
 * Source: McLernon 2016 — each prior failed complete cycle reduces odds by ~8%
 */
const PER_FAILED_CYCLE_OR = 0.92;
const MAX_FAILED_CYCLE_PENALTY_CYCLES = 4;

// ---------------------------------------------------------------------------
// Age band helpers
// ---------------------------------------------------------------------------

type AgeBand = keyof ClinicSuccessRates;

function getAgeBand(age: number): AgeBand {
  if (age < 35) return "under35";
  if (age <= 37) return "between35And37";
  if (age <= 40) return "between38And40";
  if (age <= 42) return "between41And42";
  return "over42";
}

function getAgeBandLabel(band: AgeBand): string {
  const labels: Record<AgeBand, string> = {
    under35: "under 35",
    between35And37: "35–37",
    between38And40: "38–40",
    between41And42: "41–42",
    over42: "over 42",
  };
  return labels[band];
}

/**
 * Get the base rate from success rates, falling back across adjacent bands
 * if the exact band has no data.
 */
function getBaseRate(rates: ClinicSuccessRates, band: AgeBand): number | null {
  if (rates[band] != null) return rates[band]!;

  // Try adjacent bands as fallback
  const fallbackOrder: AgeBand[] = [
    "under35",
    "between35And37",
    "between38And40",
    "between41And42",
    "over42",
  ];
  for (const fb of fallbackOrder) {
    if (rates[fb] != null) return rates[fb]!;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Core prediction
// ---------------------------------------------------------------------------

function classifyDataQuality(cyclesPerYear: number | undefined): DataQuality {
  if (!cyclesPerYear) return "low";
  if (cyclesPerYear >= 200) return "high";
  if (cyclesPerYear >= 50) return "moderate";
  return "low";
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function predictSuccessRate(
  clinic: Clinic,
  input: PredictionInput,
): PredictionResult | null {
  const ageBand = getAgeBand(input.age);
  const adjustments: Adjustment[] = [];

  // 1. Determine base rate
  let baseRate: number | null = null;
  let baseRateLabel: string;

  if (input.isUsingDonorEggs && clinic.donorSuccessRates) {
    // For donor eggs, use frozen embryos rate as default (most common)
    baseRate =
      clinic.donorSuccessRates.frozenEmbryos ??
      clinic.donorSuccessRates.freshEmbryosFreshEggs ??
      clinic.donorSuccessRates.freshEmbryosFrozenEggs ??
      clinic.donorSuccessRates.donatedEmbryos ??
      null;
    baseRateLabel = "donor egg transfers";
  } else if (
    input.priorFailedIvfCycles === 0 &&
    clinic.newPatientSuccessRates
  ) {
    // First cycle — prefer new patient rates
    baseRate = getBaseRate(clinic.newPatientSuccessRates, ageBand);
    baseRateLabel = `first-cycle patients aged ${getAgeBandLabel(ageBand)}`;
  } else if (clinic.successRates) {
    baseRate = getBaseRate(clinic.successRates, ageBand);
    baseRateLabel = `patients aged ${getAgeBandLabel(ageBand)}`;
  } else {
    baseRateLabel = "";
  }

  if (baseRate == null) return null;

  // 2. Apply adjustments
  let adjustedRate = baseRate;

  // Diagnoses
  for (const diag of input.diagnoses) {
    if (diag === "unexplained" || diag === "multiple_factors") continue;
    const or = DIAGNOSIS_OR[diag];
    adjustedRate *= or;
    adjustments.push({
      factor: DIAGNOSIS_LABELS[diag],
      direction: or > 1 ? "increase" : "decrease",
      oddsRatio: or,
    });
  }

  // BMI
  if (input.bmi && input.bmi !== "normal") {
    const or = BMI_OR[input.bmi] ?? 1.0;
    adjustedRate *= or;
    adjustments.push({
      factor: `BMI (${input.bmi})`,
      direction: "decrease",
      oddsRatio: or,
    });
  }

  // Smoking
  if (input.isSmoking) {
    adjustedRate *= SMOKING_ACTIVE_OR;
    adjustments.push({
      factor: "Active smoking",
      direction: "decrease",
      oddsRatio: SMOKING_ACTIVE_OR,
    });
  } else if (input.isFormerSmoker) {
    adjustedRate *= SMOKING_FORMER_OR;
    adjustments.push({
      factor: "Former smoker",
      direction: "decrease",
      oddsRatio: SMOKING_FORMER_OR,
    });
  }

  // Prior pregnancy
  if (input.hasPriorLiveBirth) {
    adjustedRate *= PRIOR_LIVE_BIRTH_OR;
    adjustments.push({
      factor: "Prior live birth",
      direction: "increase",
      oddsRatio: PRIOR_LIVE_BIRTH_OR,
    });
  } else if (input.hasPriorMiscarriage) {
    adjustedRate *= PRIOR_MISCARRIAGE_ONLY_OR;
    adjustments.push({
      factor: "Prior pregnancy",
      direction: "increase",
      oddsRatio: PRIOR_MISCARRIAGE_ONLY_OR,
    });
  }

  // Prior failed cycles
  if (input.priorFailedIvfCycles > 0) {
    const cappedCycles = Math.min(
      input.priorFailedIvfCycles,
      MAX_FAILED_CYCLE_PENALTY_CYCLES,
    );
    const or = Math.pow(PER_FAILED_CYCLE_OR, cappedCycles);
    adjustedRate *= or;
    adjustments.push({
      factor: `${input.priorFailedIvfCycles} prior failed cycle${input.priorFailedIvfCycles > 1 ? "s" : ""}`,
      direction: "decrease",
      oddsRatio: or,
    });
  }

  // 3. Clamp
  adjustedRate = clamp(adjustedRate, 1, 95);

  // 4. Confidence interval
  const dataQuality = classifyDataQuality(clinic.nbCyclePerYear);
  const n = clinic.nbCyclePerYear ?? 10; // default to small sample
  const p = adjustedRate / 100;
  const se = Math.sqrt((p * (1 - p)) / n);
  const uncertaintyFactor = 1 + 0.1 * adjustments.length;
  const margin = 1.96 * se * uncertaintyFactor * 100;

  const confidenceLow = clamp(Math.round(adjustedRate - margin), 1, 95);
  const confidenceHigh = clamp(Math.round(adjustedRate + margin), 1, 95);

  return {
    estimatedRate: Math.round(adjustedRate * 10) / 10,
    confidenceLow,
    confidenceHigh,
    baseRate,
    baseRateLabel: `Based on this clinic's ${baseRate}% rate for ${baseRateLabel}`,
    adjustments,
    dataQuality,
  };
}
